import { useState } from 'react'
import { Button } from 'antd'
import { ClipboardList, FilePenLine } from 'lucide-react'
import type { RentalOrder } from '@super-mega/server/contracts/order'
import { WorkspaceHeader } from '@/components/workspace-header.tsx'
import { EnterpriseSupplementModal } from '@/features/service-enterprise/index.ts'
import { RentalContractEnterpriseModal } from './rental-contract-enterprise-modal.tsx'

export function RentalContractPrerequisites({
  rentalOrderId,
  orderNo,
  missingData,
  subjectId,
  unassociatedOrder,
  canSupplement,
  onSkip
}: {
  rentalOrderId: number
  orderNo: string
  missingData: string[]
  subjectId: number | null
  unassociatedOrder?: RentalOrder
  onSkip: () => void
  canSupplement: boolean
}) {
  const [supplementOpen, setSupplementOpen] = useState(false)
  const canSupplementSubject = canSupplement && Boolean(subjectId || unassociatedOrder)
  return (
    <div className="bg-page text-content flex min-h-dvh flex-col">
      <WorkspaceHeader>
        <div className="flex min-w-0 items-center gap-3">
          <div className="bg-accent/10 text-accent flex size-9 shrink-0 items-center justify-center rounded-lg">
            <FilePenLine aria-hidden size={18} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold">生成租赁合同</div>
            <div className="text-content-muted truncate text-xs" title={orderNo}>
              订单 {orderNo}
            </div>
          </div>
        </div>
      </WorkspaceHeader>

      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-8 sm:px-6 sm:py-12 lg:py-16">
        <ol aria-label="合同生成流程" className="m-0 mb-6 grid list-none grid-cols-3 gap-3 p-0 sm:mb-8 sm:gap-6">
          <li aria-current="step" className="border-accent border-t-2 pt-3">
            <div className="text-accent mb-1 text-xs font-semibold">01 · 当前步骤</div>
            <div className="text-sm font-medium">核对签约资料</div>
          </li>
          <li className="border-border-strong text-content-soft border-t-2 pt-3">
            <div className="mb-1 text-xs">02</div>
            <div className="text-sm">配置合同信息</div>
          </li>
          <li className="border-border-strong text-content-soft border-t-2 pt-3">
            <div className="mb-1 text-xs">03</div>
            <div className="text-sm">保存并打印</div>
          </li>
        </ol>

        <section
          aria-labelledby="contract-prerequisites-title"
          className="border-border bg-surface overflow-hidden rounded-xl border shadow-xs"
        >
          <div className="p-5 sm:p-8">
            <div className="bg-accent/10 text-accent mb-5 flex size-12 items-center justify-center rounded-xl">
              <ClipboardList aria-hidden size={24} />
            </div>
            <h1 id="contract-prerequisites-title" className="m-0 text-xl font-semibold tracking-tight sm:text-2xl">
              订单资料待补齐，可跳过继续生成合同
            </h1>
            <p className="text-content-muted mt-3 mb-6 text-sm leading-6">
              当前订单还有 {missingData.length} 项资料待补齐。可补齐后继续，也可跳过；未匹配的合同字段将以空格填充。
            </p>

            <div className="border-border overflow-hidden rounded-lg border">
              <div className="bg-surface-muted border-border flex items-center justify-between gap-3 border-b px-4 py-3">
                <h2 className="m-0 text-sm font-medium">待补齐资料</h2>
                <span className="text-content-muted text-xs">共 {missingData.length} 项</span>
              </div>
              <ul className="divide-border m-0 list-none divide-y p-0">
                {missingData.map((item) => (
                  <li key={item} className="flex items-start justify-between gap-4 px-4 py-4">
                    <span className="min-w-0 text-sm leading-6 break-words">{item}</span>
                    <span className="bg-surface-muted text-content-muted shrink-0 rounded px-2 py-1 text-xs">
                      待补齐
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="border-border bg-surface-muted flex w-full items-center justify-between gap-3 border-t px-5 py-4 sm:px-8">
            <p className="text-content-muted m-0 shrink-0 text-xs leading-5">
              {canSupplementSubject
                ? '可在此补齐服务对象信息，也可跳过继续生成合同。'
                : '可跳过资料补齐，继续生成合同。'}
            </p>
            <div className="flex w-full justify-end gap-2 sm:flex-row sm:flex-wrap">
              <Button size="medium" onClick={onSkip}>
                跳过，继续生成合同
              </Button>
              {canSupplementSubject && (
                <Button size="medium" type="primary" onClick={() => setSupplementOpen(true)}>
                  补齐服务对象信息
                </Button>
              )}
            </div>
          </div>
        </section>
        <p className="text-content-soft mt-5 text-center text-xs leading-5">
          合同将使用订单中的签约资料，请确保信息完整、准确。
        </p>
      </main>
      {supplementOpen && !subjectId && unassociatedOrder && (
        <RentalContractEnterpriseModal order={unassociatedOrder} onOpenChange={setSupplementOpen} />
      )}
      {supplementOpen && subjectId && (
        <EnterpriseSupplementModal
          key={subjectId}
          subjectId={subjectId}
          open={supplementOpen}
          onOpenChange={setSupplementOpen}
          currentOrder={{ type: 'rental', id: rentalOrderId }}
          syncCurrentRentalOrder
        />
      )}
    </div>
  )
}
